import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { GameFrame, GameShell, Spinner } from './EnvLayout';
import { useAdminMode } from './useAdminMode';
import { htmlGameLoader } from './gameRegistry';
import AdminPlant from './AdminPlant';
import { ui } from './envUi';

/** The Plant game page: injects the admin-chosen minutes per day, plus an in-game admin editor. */
function PlantFrame() {
  const { data } = trpc.environmental.plantSettings.useQuery();
  const loader = htmlGameLoader('plant');
  const [html, setHtml] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    loader?.().then(h => { if (alive) setHtml(h); });
    return () => { alive = false; };
  }, [loader]);

  const doc = useMemo(() => {
    if (!html || !data) return null;
    // Function replacement so nothing in the value is read as a replacement pattern
    return html.replace('<script>', () => `<script>window.__PLANT_MINUTES_PER_DAY__=${Number(data.minutesPerDay)};</script>\n<script>`);
  }, [html, data]);

  if (!loader) return <p className="px-4 py-24 text-center text-slate-600 dark:text-slate-300">The Plant game is not available yet.</p>;
  if (!doc) return <Spinner />;
  return <GameFrame html={doc} title="Plant" />;
}

export default function PlantGame({ title }: { title: string }) {
  const { editing, actions, loginModal } = useAdminMode('Plant');

  return (
    <GameShell title={title} right={actions}>
      {editing ? (
        <div className="mx-auto max-w-3xl px-4 py-6 md:py-10">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-yellow-400 px-3 py-1 text-xs font-bold uppercase tracking-wide text-black">Admin mode</span>
              <span className={ui.muted}>Changes apply the next time the game opens.</span>
            </div>
            <Link to="/environmental/admin" className={ui.btnGhost}>All admin settings</Link>
          </div>
          <AdminPlant />
        </div>
      ) : (
        <PlantFrame />
      )}
      {loginModal}
    </GameShell>
  );
}
